import React from 'react';
import { Plus, Trash2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Badge, Button } from '../../components/ui';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';

interface StepListSidebarProps {
  steps: WorkflowBuilderStep[];
  selectedStep: string | null;
  stepLabels: Map<string, string>;
  stepResults: Record<string, unknown>;
  /** Per-step debug status: 'has_output' | 'drifted' | undefined */
  debugStatuses?: Record<string, 'has_output' | 'drifted'>;
  /** Per-step validation warnings keyed by step id */
  stepWarnings?: Record<string, Array<{ message: string; severity: 'error' | 'warning' }>>;
  onSelectStep: (stepId: string) => void;
  onAddStep: () => void;
  onRemoveStep: (stepId: string) => void;
}

export const StepListSidebar: React.FC<StepListSidebarProps> = ({
  steps,
  selectedStep,
  stepLabels,
  stepResults,
  debugStatuses,
  stepWarnings,
  onSelectStep,
  onAddStep,
  onRemoveStep,
}) => (
  <div className="h-full w-full overflow-y-auto border-t p-3">
    <div className="flex justify-between items-center mb-2">
      <span className="font-medium text-heading text-xs">Steps ({steps.length})</span>
      <Button variant="primary" size="sm" onClick={onAddStep}>
        <Plus size={10} /> Add
      </Button>
    </div>
    <div className="space-y-0.5">
      {steps.map((step, i) => {
        const stepKey = step.id.toString();
        return (
          <div
            key={stepKey}
            onClick={() => onSelectStep(stepKey)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm cursor-pointer text-xs ${selectedStep === stepKey ? 'bg-info-muted border border-accent' : 'hover:bg-surface-hover'}`}
          >
            <span className="text-faint w-6 shrink-0 text-right font-mono text-[10px]">
              {stepLabels.get(stepKey) || i + 1}
            </span>
            <span className="flex-1 text-heading truncate">{step.name || `Step ${i + 1}`}</span>
            {stepWarnings?.[stepKey]?.length ? (
              <span
                title={stepWarnings[stepKey].map((w) => w.message).join(' \u2022 ')}
                className={stepWarnings[stepKey].some((w) => w.severity === 'error') ? 'text-danger' : 'text-warning'}
              >
                <AlertTriangle size={10} className="shrink-0" />
              </span>
            ) : null}
            {debugStatuses?.[stepKey] === 'drifted' ? (
              <span title="Contract changed since last debug">
                <AlertTriangle size={10} className="text-warning shrink-0" />
              </span>
            ) : stepResults[stepKey] !== undefined ? (
              <span title="Has debug output">
                <CheckCircle size={10} className="text-success shrink-0" />
              </span>
            ) : null}
            <Badge
              variant={
                step.executionMode === 'parallel'
                  ? 'purple'
                  : step.executionMode === 'conditional'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {(step.executionMode ?? 'seq').slice(0, 3)}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="size-6 shrink-0 text-danger"
              aria-label={`Remove ${step.name || `step ${i + 1}`}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveStep(stepKey);
              }}
            >
              <Trash2 size={10} />
            </Button>
          </div>
        );
      })}
      {steps.length === 0 && <div className="text-center py-4 text-muted text-xs">No steps. Click + Add.</div>}
    </div>
  </div>
);
